import { IGridStateSettings } from '@models/response/IGridStateSettings';
import { DataTableWrapMode, isDataTableWrapMode } from '@srm/ui/data-table';
import { AttachView, IAttachSort } from './AttachGrid.types';

/** Сохранённый вид грида: режим, перенос, видимость колонок и сортировка. */
export interface IAttachSavedView {
    name: string;
    view: AttachView;
    /** Высота строк — канон базовой таблицы (компактная/средняя/полная). */
    wrapMode: DataTableWrapMode;
    hidden: string[];
    sort: IAttachSort[];
    /** Применяется автоматически при открытии таблицы. */
    default?: boolean;
}

const isAttachView = (v: unknown): v is AttachView => v === 'table' || v === 'cards' || v === 'compact';

/** Терпимый разбор сортировки: наш формат `{field,dir}` или легаси DevExtreme `{selector,desc}`. */
function parseSort(raw: unknown): IAttachSort[] {
    if (!Array.isArray(raw)) return [];
    const out: IAttachSort[] = [];
    for (const it of raw) {
        if (!it || typeof it !== 'object') continue;
        const o = it as Record<string, unknown>;
        const field = (o.field ?? o.selector) as string | undefined;
        if (!field) continue;
        out.push({ field, dir: o.dir === 'desc' || o.desc === true ? 'desc' : 'asc' });
    }
    return out;
}

/** Вид → общая серверная схема состояния (`settings/table`). Специфика AttachGrid
 *  (режим/перенос) — в `state.attachView`/`state.wrap`; видимость — в `state.columns`. */
export function toGridStateSettings(v: IAttachSavedView): IGridStateSettings {
    return {
        name: v.name,
        default: !!v.default,
        selected: false,
        state: {
            columns: v.hidden.map((dataField) => ({ dataField, visible: false })),
            allowedPageSizes: [],
            filterPanel: { filterEnabled: false },
            filterValue: '',
            searchText: '',
            pageIndex: 0,
            pageSize: 0,
            attachView: v.view,
            // легаси-поле держим согласованным — на случай отката фронта
            wrap: v.wrapMode !== 'nowrap',
            wrapMode: v.wrapMode,
        },
        filters: undefined,
        sort: v.sort,
    };
}

/** Серверная схема → вид (терпимо к легаси-состояниям DevExtreme того же ключа). */
export function fromGridStateSettings(s: IGridStateSettings): IAttachSavedView {
    const cols = s.state?.columns ?? [];
    return {
        name: s.name,
        view: isAttachView(s.state?.attachView) ? s.state.attachView : 'table',
        // старые виды несут булев wrap — читаем его как fallback
        wrapMode: isDataTableWrapMode(s.state?.wrapMode)
            ? s.state.wrapMode
            : s.state?.wrap === false
              ? 'nowrap'
              : 'wrap',
        hidden: cols.filter((c) => c.visible === false && !!c.dataField).map((c) => c.dataField as string),
        sort: parseSort(s.sort),
        default: !!s.default,
    };
}
